/**
 * Minimal, dependency-free, XSS-safe Markdown renderer for assistant messages.
 *
 * Safety model: the raw string is HTML-escaped FIRST, so no model-produced HTML
 * (or javascript: URLs) can ever reach the DOM. Only our own whitelisted tags
 * (strong, code, pre, ul/ol/li, table, h3/h4, p, br) are then inserted around
 * the already-escaped text. The result is safe for v-html.
 *
 * Supported subset: fenced code blocks, GitHub tables, ordered/unordered lists
 * (one level of nesting via indentation), h1-h6 (rendered as h3/h4), bold,
 * inline code, paragraphs.
 */

const CODE_TOKEN_PREFIX = "@@CODEBLOCK_";
const CODE_TOKEN_SUFFIX = "@@";

const ALLOWED_CHART_TYPES = ["bar", "line", "doughnut"] as const;
const MAX_CHART_LABELS = 24;
const MAX_CHART_SERIES = 6;

export type AiChartType = (typeof ALLOWED_CHART_TYPES)[number];

export interface AiChartSeries {
    name: string;
    data: number[];
}

export interface AiChartSpec {
    type: AiChartType;
    title: string | null;
    labels: string[];
    series: AiChartSeries[];
}

export interface AiStatsSpec {
    compliant: number;
    nonCompliant: number;
    complianceRate: number;
    before: { compliant: number; nonCompliant: number } | null;
}

export type AiSegment =
    | { kind: "markdown"; html: string }
    | { kind: "chart"; spec: AiChartSpec }
    | { kind: "stats"; spec: AiStatsSpec };

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

/** Inline formatting on an ALREADY html-escaped string. */
function renderInline(text: string): string {
    return text
        .replace(/`([^`]+)`/g, (_match, code: string) => `<code>${code}</code>`)
        .replace(
            /\*\*([^*]+)\*\*/g,
            (_match, bold: string) => `<strong>${bold}</strong>`,
        );
}

function isTableSeparator(line: string): boolean {
    return /^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/.test(line) && line.includes("-");
}

function splitTableCells(line: string): string[] {
    return line
        .trim()
        .replace(/^\||\|$/g, "")
        .split("|")
        .map((cell) => cell.trim());
}

function renderTable(headerLine: string, rows: string[]): string {
    const headers = splitTableCells(headerLine)
        .map((cell) => `<th>${renderInline(cell)}</th>`)
        .join("");

    const body = rows
        .map((row) => {
            const cells = splitTableCells(row)
                .map((cell) => `<td>${renderInline(cell)}</td>`)
                .join("");
            return `<tr>${cells}</tr>`;
        })
        .join("");

    return `<div class="ai-md-table"><table><thead><tr>${headers}</tr></thead><tbody>${body}</tbody></table></div>`;
}

interface ListItem {
    indent: number;
    ordered: boolean;
    content: string;
}

function renderList(items: ListItem[]): string {
    let html = "";
    const stack: boolean[] = [];
    let prevDepth = -1;

    const openList = (ordered: boolean): void => {
        html += ordered ? "<ol>" : "<ul>";
        stack.push(ordered);
    };
    const closeList = (): void => {
        html += (stack.pop() ?? false) ? "</ol>" : "</ul>";
    };

    items.forEach((item) => {
        const depth = item.indent >= 2 ? 1 : 0;

        if (depth > prevDepth) {
            for (let d = prevDepth + 1; d <= depth; d += 1) {
                openList(item.ordered);
            }
        } else if (depth < prevDepth) {
            for (let d = prevDepth; d > depth; d -= 1) {
                html += "</li>";
                closeList();
            }
            html += "</li>";
        } else {
            html += "</li>";
        }

        html += `<li>${renderInline(item.content)}`;
        prevDepth = depth;
    });

    for (let d = prevDepth; d >= 0; d -= 1) {
        html += "</li>";
        closeList();
    }

    return html;
}

export function renderMarkdown(source: string): string {
    const escaped = escapeHtml((source ?? "").replace(/\r\n/g, "\n"));

    // Extract fenced code blocks so their content is not treated as markdown.
    const codeBlocks: string[] = [];
    const withoutCode = escaped.replace(
        /```[^\n]*\n([\s\S]*?)```/g,
        (_match, code: string) => {
            const index =
                codeBlocks.push(
                    `<pre><code>${code.replace(/\n$/, "")}</code></pre>`,
                ) - 1;
            return `${CODE_TOKEN_PREFIX}${index}${CODE_TOKEN_SUFFIX}`;
        },
    );

    const lines = withoutCode.split("\n");
    const blocks: string[] = [];
    let paragraph: string[] = [];
    let listItems: ListItem[] = [];

    const flushParagraph = (): void => {
        if (paragraph.length > 0) {
            blocks.push(`<p>${paragraph.map(renderInline).join("<br>")}</p>`);
            paragraph = [];
        }
    };

    const flushList = (): void => {
        if (listItems.length > 0) {
            blocks.push(renderList(listItems));
            listItems = [];
        }
    };

    const isCodeToken = (value: string): boolean =>
        value.startsWith(CODE_TOKEN_PREFIX) &&
        value.endsWith(CODE_TOKEN_SUFFIX);

    for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        const trimmed = line.trim();

        if (trimmed === "") {
            flushParagraph();

            // A blank line only ends a list if the next non-empty line is not
            // itself a list item (otherwise numbering would restart per block).
            if (listItems.length > 0) {
                let k = i + 1;
                while (k < lines.length && lines[k].trim() === "") {
                    k += 1;
                }
                const next = k < lines.length ? lines[k] : "";
                if (!/^\s*([-*]|\d+\.)\s+/.test(next)) {
                    flushList();
                }
            }

            continue;
        }

        if (isCodeToken(trimmed)) {
            flushParagraph();
            flushList();
            blocks.push(trimmed);
            continue;
        }

        // Table: header row followed by a separator row.
        if (
            line.includes("|") &&
            i + 1 < lines.length &&
            isTableSeparator(lines[i + 1])
        ) {
            flushParagraph();
            flushList();
            const rows: string[] = [];
            let j = i + 2;
            while (
                j < lines.length &&
                lines[j].includes("|") &&
                lines[j].trim() !== ""
            ) {
                rows.push(lines[j]);
                j += 1;
            }
            blocks.push(renderTable(line, rows));
            i = j - 1;
            continue;
        }

        const heading = /^(#{1,6})\s+(.*)$/.exec(line);
        if (heading) {
            flushParagraph();
            flushList();
            const level = Math.min(heading[1].length + 2, 4); // h3/h4 only
            blocks.push(`<h${level}>${renderInline(heading[2])}</h${level}>`);
            continue;
        }

        const unordered = /^(\s*)[-*]\s+(.*)$/.exec(line);
        const ordered = /^(\s*)\d+\.\s+(.*)$/.exec(line);
        if (unordered || ordered) {
            flushParagraph();
            const match = (unordered ?? ordered) as RegExpExecArray;
            listItems.push({
                indent: match[1].length,
                ordered: ordered !== null,
                content: match[2],
            });
            continue;
        }

        flushList();
        paragraph.push(trimmed);
    }

    flushParagraph();
    flushList();

    let html = blocks.join("");
    codeBlocks.forEach((block, index) => {
        html = html
            .split(`${CODE_TOKEN_PREFIX}${index}${CODE_TOKEN_SUFFIX}`)
            .join(block);
    });

    return html;
}

function toStringArray(value: unknown, max: number): string[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.slice(0, max).map((item) => String(item));
}

/** Validate a model-supplied chart spec. Returns null if it is not usable. */
function validateChartSpec(raw: unknown): AiChartSpec | null {
    if (typeof raw !== "object" || raw === null) {
        return null;
    }

    const candidate = raw as Record<string, unknown>;
    const type = candidate.type;

    if (
        typeof type !== "string" ||
        !ALLOWED_CHART_TYPES.includes(type as AiChartType)
    ) {
        return null;
    }

    const labels = toStringArray(candidate.labels, MAX_CHART_LABELS);
    if (labels.length === 0) {
        return null;
    }

    if (!Array.isArray(candidate.series)) {
        return null;
    }

    const series: AiChartSeries[] = [];
    for (const entry of candidate.series.slice(0, MAX_CHART_SERIES)) {
        if (typeof entry !== "object" || entry === null) {
            continue;
        }
        const seriesEntry = entry as Record<string, unknown>;
        if (!Array.isArray(seriesEntry.data)) {
            continue;
        }
        const data = seriesEntry.data
            .slice(0, MAX_CHART_LABELS)
            .map((value) =>
                typeof value === "number" && isFinite(value) ? value : 0,
            );
        series.push({
            name: typeof seriesEntry.name === "string" ? seriesEntry.name : "",
            data,
        });
    }

    if (series.length === 0) {
        return null;
    }

    return {
        type: type as AiChartType,
        title: typeof candidate.title === "string" ? candidate.title : null,
        labels,
        series,
    };
}

function toCount(value: unknown): number {
    return typeof value === "number" && isFinite(value) && value >= 0
        ? value
        : 0;
}

/** Validate a model-supplied dashboard-stats spec. Returns null if unusable. */
function validateStatsSpec(raw: unknown): AiStatsSpec | null {
    if (typeof raw !== "object" || raw === null) {
        return null;
    }

    const candidate = raw as Record<string, unknown>;
    const compliant = toCount(candidate.compliant);
    const nonCompliant = toCount(candidate.nonCompliant);

    if (compliant + nonCompliant === 0) {
        return null;
    }

    const rate =
        typeof candidate.complianceRate === "number" &&
        isFinite(candidate.complianceRate)
            ? candidate.complianceRate
            : Math.round((compliant / (compliant + nonCompliant)) * 100);

    let before: AiStatsSpec["before"] = null;
    if (typeof candidate.before === "object" && candidate.before !== null) {
        const raw2 = candidate.before as Record<string, unknown>;
        before = {
            compliant: toCount(raw2.compliant),
            nonCompliant: toCount(raw2.nonCompliant),
        };
    }

    return { compliant, nonCompliant, complianceRate: rate, before };
}

/**
 * Split assistant content into ordered segments. Fenced ```dmarceye-chart and
 * ```dmarceye-stats blocks carry validated JSON specs (rendered as a chart or a
 * dashboard bar); anything else — or anything that fails validation — is shown
 * as normal markdown.
 */
export function parseAssistantContent(source: string): AiSegment[] {
    const text = (source ?? "").replace(/\r\n/g, "\n");
    // Match ANY fenced block; we decide by its CONTENT, not by the language tag,
    // so a stats/chart spec renders as a component even if the model wraps it in
    // a plain ``` or ```json fence instead of ```dmarceye-stats / ```dmarceye-chart.
    const pattern = /```([^\n]*)\n([\s\S]*?)```/g;
    const segments: AiSegment[] = [];

    let lastIndex = 0;
    let match: RegExpExecArray | null;

    const pushMarkdown = (chunk: string): void => {
        if (chunk.trim() !== "") {
            segments.push({ kind: "markdown", html: renderMarkdown(chunk) });
        }
    };

    while ((match = pattern.exec(text)) !== null) {
        pushMarkdown(text.slice(lastIndex, match.index));
        lastIndex = pattern.lastIndex;

        let parsed: unknown = null;
        try {
            parsed = JSON.parse(match[2].trim());
        } catch {
            parsed = null;
        }

        const chartSpec = validateChartSpec(parsed);
        if (chartSpec !== null) {
            segments.push({ kind: "chart", spec: chartSpec });
            continue;
        }

        const statsSpec = validateStatsSpec(parsed);
        if (statsSpec !== null) {
            segments.push({ kind: "stats", spec: statsSpec });
            continue;
        }

        // Not a recognised spec — keep the original fence as a normal code block.
        pushMarkdown(match[0]);
    }

    pushMarkdown(text.slice(lastIndex));

    if (segments.length === 0) {
        segments.push({ kind: "markdown", html: renderMarkdown(text) });
    }

    return segments;
}
